import React, { Component } from 'react';
import * as Sentry from '@sentry/browser';
import _ from 'lodash';
import swal from 'sweetalert';
import classNames from 'classnames';
import scrollToElement from 'scroll-to-element';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { Collapse } from 'reactstrap';

import { stripeApiKey } from 'config/stripe-api-key';
import PlanCard from 'components/shared/plan-card';
import Modal from 'components/shared/modal';
import StripeForm from 'components/shared/stripe-form';
import actions from 'api/actions';
import learningCenterIdentifier from 'utils/learning-center-identifier';
import styles from './styles.module.scss';

const stripePromise = loadStripe(stripeApiKey);

function openIntercomMessage(message) {
  return () => {
    if (window && window.Intercom) {
      window.Intercom('showNewMessage', message);
    }
  };
}

class OnboardingSchedule extends Component {
  constructor(props) {
    super(props);
    this.state = {
      plans: [],
      planSelectCollapseOpen: true,
      selectedPlan: null,
      checkingOut: false,
      checkoutComplete: false,
      networkRequest: false,
      discount_code: '',
    };

    this.togglePlanSelectCollapse = this.togglePlanSelectCollapse.bind(this);
    this.togglePlanSelect = this.togglePlanSelect.bind(this);
    this.toggleCheckout = this.toggleCheckout.bind(this);
    this.onComplete = this.onComplete.bind(this);
    this.redirectHome = this.redirectHome.bind(this);
    this.onDiscountApply = this.onDiscountApply.bind(this);
    this.onDiscountCodeChange = this.onDiscountCodeChange.bind(this);
  }

  componentDidMount() {
    const { learningCenter } = this.props.location.state;

    actions
      .get('plans')
      .then(r => {
        const plans = _.filter(
          r.data,
          plan =>
            _.includes(plan.learning_center_ids, learningCenter.id) ||
            plan.learning_center_ids.length === 0,
        );
        this.setState({ plans });
      })
      .catch(e => Sentry.captureException(e));
  }

  onDiscountApply(code) {
    const { discount_code, selectedPlan } = this.state;

    actions
      .rawPost(`discounts`, { discount_code, plan_id: selectedPlan.id })
      .then(r => this.setState({ appliedDiscount: r.data }))
      .catch(e => {
        this.setState({ discountCodeFailure: true });
        Sentry.captureException(e);
      });
  }

  onDiscountCodeChange(e) {
    this.setState({ discount_code: e.target.value });
  }

  togglePlanSelectCollapse() {
    this.setState(prevState => ({
      planSelectCollapseOpen: !prevState.planSelectCollapseOpen,
    }));
  }

  togglePlanSelect(plan) {
    return () => {
      this.setState({ selectedPlan: plan }, () => {
        scrollToElement('#selectedPlan');
        this.setState({ checkingOut: true });
      });
    };
  }

  toggleCheckout() {
    this.setState(prevState => ({ checkingOut: !prevState.checkingOut }));
  }

  onComplete(planParams) {
    const { selectedSlot, learningCenter } = this.props.location.state;

    this.setState({ networkRequest: true }, () =>
      actions
        .post('learning-center-plan-select', {
          plan_params: planParams,
          event_params: { ...selectedSlot, parent_email: planParams.email },
        })
        .then(r =>
          this.setState({
            checkingOut: false,
            checkoutComplete: true,
            networkRequest: false,
          }),
        )
        .catch(e => {
          Sentry.captureException(e);

          if (e && e.response && e.response.data && e.response.data.code) {
            const { code } = e.response.data;

            if (code === 'stripe_error') {
              swal(
                'Payment Error',
                e.response.data.extra_error_information,
                'error',
              );
            } else if (code === 'parent_create_error') {
              swal(
                'Error Creating Account',
                "Please refresh your page and try again. If you're still unable to checkout, please contact info@unyted.co",
                'error',
              );
            } else if (code === 'schedule_conflict') {
              const content = document.createElement('div');
              content.innerHTML = `Sorry, it looks like there's an event scheduled for this time. Please visit <a href=${`/unyted-learning-centers/${learningCenterIdentifier(learningCenter)}`}>the page for this learning center here</a> and try a different time.`;

              swal({
                content,
                title: 'Schedule Conflict',
                icon: 'error',
              });
            } else if (code === 'unknown_error') {
              swal(
                'Unknown Error',
                "Unknown Error, please refresh and try again. If you're still unable to checkout, please contact info@unyted.co",
                'error',
              );
            } else {
              swal(
                'Unknown Error',
                "Unknown Error, please refresh and try again. If you're still unable to checkout, please contact info@unyted.co",
                'error',
              );
            }
          } else {
            swal(
              'Unknown Error',
              'Sorry, something went wrong. Please refresh and try again',
            );
          }
          this.setState({ networkRequest: false });
        }),
    );
  }

  redirectHome() {
    this.props.history.push('/');
    return;
  }

  render() {
    const {
      plans,
      planSelectCollapseOpen,
      selectedPlan,
      checkingOut,
      checkoutComplete,
      networkRequest,
      discount_code,
      appliedDiscount,
      discountCodeFailure,
    } = this.state;

    const { location, history } = this.props;

    if (
      !location ||
      !location.state ||
      (location && location.state && !location.state.selectedSlot)
    ) {
      history.goBack();
      return;
    }

    const { selectedSlot, learningCenter } = location.state;

    return (
      <div className={styles.firstSessionCheckout}>
        <section className={styles.planSelect}>
          <div className={styles.sessionDetails}>
            <div className={styles.infoSummary}>
              <h3>First session</h3>
              <ul className="list-unstyled">
                <li>
                  <strong>Center:</strong> {learningCenter.title}
                </li>
                <li>
                  <strong>Instructor:</strong>{' '}
                  {learningCenter.instructor_first_name}
                </li>
                <li>
                  <strong>Date:</strong>{' '}
                  {moment(selectedSlot.date).format('MMM D YYYY')}
                </li>
                <li>
                  <strong>Time:</strong>{' '}
                  {moment(
                    `${selectedSlot.date} ${selectedSlot.start_time}`,
                  ).format('h:mma')}
                </li>
                <li>
                  <strong>Duration:</strong> {selectedSlot.duration} minutes
                </li>
                <li>
                  <strong>Location:</strong> {selectedSlot.location}
                </li>
              </ul>
            </div>
            <div id="selectedPlan" className={styles.planSelectedContainer}>
              <h3>Learning Service Details</h3>
              <ul className="list-unstyled">
                <li>
                  <strong>Service:</strong>{' '}
                  {selectedPlan ? (
                    selectedPlan.title
                  ) : (
                    <span className={styles.actionRequired}>
                      &lt;Select a service below&gt;
                    </span>
                  )}
                </li>
                <li>
                  <strong>Instruction Hours:</strong>{' '}
                  {selectedPlan ? (
                    selectedPlan.hours
                  ) : (
                    <span className={styles.actionRequired}>
                      &lt;Select a service below&gt;
                    </span>
                  )}
                </li>
                <li>
                  <strong>Price:</strong>{' '}
                  {selectedPlan ? (
                    `$${selectedPlan.price}`
                  ) : (
                    <span className={styles.actionRequired}>
                      &lt;Select a service below&gt;
                    </span>
                  )}
                </li>
              </ul>
              <button
                id="confirmPlan"
                type="button"
                onClick={this.toggleCheckout}
                disabled={!selectedPlan}
                className={classNames({
                  [styles.confirmPlanButton]: true,
                  [styles.disabled]: !selectedPlan,
                })}
              >
                Confirm Service{' '}
                <FontAwesomeIcon
                  icon={selectedPlan ? 'play' : 'lock'}
                  className="ml-1"
                />
              </button>
            </div>
          </div>
          <div className={styles.collapsible}>
            <div
              className={styles.collapseBar}
              onClick={this.togglePlanSelectCollapse}
            >
              <h3 className="col-12">
                <span className={selectedPlan ? '' : 'text-blue'}>
                  Step 3 of 4: Select a Service
                  {selectedPlan && (
                    <FontAwesomeIcon icon="check" className="text-apple ml-2" />
                  )}
                </span>
                <FontAwesomeIcon
                  icon={planSelectCollapseOpen ? 'chevron-up' : 'chevron-down'}
                  className={selectedPlan ? '' : 'text-blue'}
                />
              </h3>
            </div>
            <Collapse isOpen={planSelectCollapseOpen}>
              <div className={styles.planSelectContainer}>
                {plans.map(plan => (
                  <div key={plan.id}>
                    <PlanCard
                      plan={plan}
                      learningCenter={learningCenter.title}
                      onCtaClick={this.togglePlanSelect(plan)}
                      ctaButtonText="Select This Service"
                      className={classNames({
                        [styles.selected]:
                          _.get(selectedPlan, 'id') === plan.id,
                      })}
                    />
                  </div>
                ))}
              </div>
            </Collapse>
          </div>
          {checkingOut && !checkoutComplete && (
            <Modal
              size="xl"
              heading="Step 4 of 4: Complete Payment"
              onAccept={this.toggleCheckout}
              onClose={this.toggleCheckout}
              noFooter
            >
              <div className={styles.checkoutModal}>
                <div className="row">
                  <div className="col-lg-6">
                    <h3>First session:</h3>
                    <ul className="list-unstyled">
                      <li>
                        <strong>Center:</strong> {learningCenter.title}
                      </li>
                      <li>
                        <strong>Instructor:</strong>{' '}
                        {learningCenter.instructor_first_name}
                      </li>
                      <li>
                        <strong>Date:</strong>{' '}
                        {moment(selectedSlot.date).format('MMM D YYYY')}
                      </li>
                      <li>
                        <strong>Time:</strong>{' '}
                        {moment(
                          `${selectedSlot.date} ${selectedSlot.start_time}`,
                        ).format('h:mma')}
                      </li>
                      <li>
                        <strong>Duration:</strong> {selectedSlot.duration}{' '}
                        minutes
                      </li>
                      <li>
                        <strong>Location:</strong> {selectedSlot.location}
                      </li>
                    </ul>
                    <h3>Learning Service:</h3>
                    <ul className="list-unstyled">
                      <li>
                        <strong>Service:</strong> {selectedPlan.title}
                      </li>
                      <li>
                        <strong>Instruction Hours:</strong> {selectedPlan.hours}
                      </li>
                      <li>
                        <strong>Price:</strong> ${selectedPlan.price}
                      </li>
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <h3>
                      Due Now:&nbsp;
                      {appliedDiscount ? (
                        <React.Fragment>
                          <s className="text-gray faded">
                            ${selectedPlan.price}
                          </s>
                          &nbsp;
                          <span className="text-green">
                            $
                            {selectedPlan.price -
                              selectedPlan.price *
                                appliedDiscount.discount_percentage}
                          </span>
                        </React.Fragment>
                      ) : (
                        <span>${selectedPlan.price}</span>
                      )}
                    </h3>
                    {appliedDiscount ? (
                      <p>Discount Applied: {appliedDiscount.code}</p>
                    ) : (
                      <div className="form-group flex-column d-flex ">
                        <label htmlFor="discount_code">Discount Code</label>
                        <input
                          type="text"
                          className="form-control"
                          name="discount_code"
                          placeholder="Enter Discount Code"
                          value={discount_code}
                          onChange={this.onDiscountCodeChange}
                        />
                        <button
                          type="button"
                          className="green mt-3 w-50 align-self-center"
                          onClick={this.onDiscountApply}
                        >
                          Apply Code
                        </button>
                        {discountCodeFailure && (
                          <p color="mt-3 text-red">Invalid Discount Code</p>
                        )}
                      </div>
                    )}
                    <Elements stripe={stripePromise}>
                      <ElementsConsumer>
                        {({ stripe, elements }) => (
                          <StripeForm
                            stripe={stripe}
                            elements={elements}
                            modalDisabled={networkRequest}
                            onComplete={this.onComplete}
                            planId={selectedPlan.id}
                            disablePaymentElements={
                              appliedDiscount &&
                              selectedPlan.price -
                                selectedPlan.price *
                                  appliedDiscount.discount_percentage ===
                                0
                            }
                            appliedDiscount={appliedDiscount}
                          />
                        )}
                      </ElementsConsumer>
                    </Elements>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {!checkingOut && checkoutComplete && (
            <Modal
              size="md"
              heading={<h3>You're ready to onboard!</h3>}
              onAccept={this.redirectHome}
              onClose={this.redirectHome}
              noFooter
            >
              <p>
                Your <strong>first session</strong> is{' '}
                {moment(
                  `${selectedSlot.date} ${selectedSlot.start_time}`,
                ).format('MMM D YYYY [at] h:mma')}{' '}
                with {learningCenter.instructor_first_name} at{' '}
                {selectedSlot.location}.
              </p>
              <p>
                <strong>Please check your email*</strong> for next steps on
                logging in to your parent account.
              </p>
              <p>
                If you have any further questions, feel free to&nbsp;
                <button
                  className="link text-blue"
                  onClick={openIntercomMessage(
                    "I'm a parent and I have a question about my first Unyted Learning Center session",
                  )}
                >
                  contact us here
                </button>
                .
              </p>
              <p>*You may need to check your spam folder.</p>
            </Modal>
          )}
        </section>
      </div>
    );
  }
}

export default OnboardingSchedule;
